import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import {
  fetchMappingCredentialStatus,
  saveMappingCredentials,
  type CredentialField,
  type MappingCredentialsInput,
} from '../../api/dataApi';
import { AppCard } from '../ui/AppCard';
import { Button } from '../ui/Button';

// Only a source that fetches a URL has credentials to configure - the
// agent-pulled sourceTypes, and the Scraper connector's scrape_* ones
// (internal/scrapequeue); a webhook receives, so it has none.
export const CREDENTIAL_SOURCE_TYPES = [
  'json', 'csv', 'tsv', 'xml', 'yaml', 'lines', 'prometheus',
  'scrape_json', 'scrape_csv', 'scrape_tsv', 'scrape_xml', 'scrape_yaml', 'scrape_prometheus', 'scrape_html',
];

// "Name: value" per line -> {Name: value}; blank lines ignored.
export function parseHeaderLines(text: string): { headers: Record<string, string>; problem: string | null } {
  const headers: Record<string, string> = {};
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const i = line.indexOf(':');
    if (i < 1) return { headers: {}, problem: `“${line}” is not “Name: value”.` };
    headers[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return { headers, problem: null };
}

const LABEL: Record<CredentialField, string> = {
  bearerToken: 'Bearer token',
  basicAuth: 'Basic auth',
  headers: 'Custom headers',
};

const inputCls =
  'rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3 py-2 text-sm outline-none focus:border-blue-500';

// Scrape credentials for one Integration (roadmap #9 Phase 4, CredentialRef).
// WRITE-ONLY by design: this can set or remove a credential and shows which
// are configured, but the backend never returns a value to the browser - so it
// cannot be copied out of the page, a screenshot or the network tab later.
// They are stored apart from the Mapping spec, so they are also absent from
// its JSON, its drafts and its version history.
export function CredentialsCard({ nickname }: { nickname: string }) {
  const [configured, setConfigured] = useState<CredentialField[] | null>(null);
  const [bearer, setBearer] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMappingCredentialStatus(nickname)
      .then((c) => alive && setConfigured(c))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Unable to load credentials.'));
    return () => {
      alive = false;
    };
  }, [nickname]);

  if (configured === null) return error ? <p className="text-xs text-red-500 m-0">{error}</p> : null;

  const send = async (input: MappingCredentialsInput) => {
    setSaving(true);
    setError(null);
    setSavedNote(false);
    try {
      setConfigured(await saveMappingCredentials(nickname, input));
      setBearer('');
      setPass('');
      setHeaderText('');
      setSavedNote(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const input: MappingCredentialsInput = {};
    if (bearer.trim()) input.bearerToken = bearer.trim();
    if (user.trim() || pass) input.basicAuth = { username: user.trim(), password: pass };
    if (headerText.trim()) {
      const { headers, problem } = parseHeaderLines(headerText);
      if (problem) {
        setError(problem);
        return;
      }
      input.headers = headers;
    }
    if (Object.keys(input).length === 0) {
      setError('Enter a value to save.');
      return;
    }
    void send(input);
  };

  const dirty = !!(bearer.trim() || user.trim() || pass || headerText.trim());

  return (
    <AppCard className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <KeyRound size={15} className="text-black/50 dark:text-white/50" />
        <span className="text-sm font-semibold">Scrape credentials</span>
      </div>
      <span className="text-xs text-black/50 dark:text-white/50">
        Used when this integration’s source is fetched - by your agent, or by Zmist itself for a Scraper mapping.
        They are stored separately from the mapping and can’t be read back here - enter a new value to replace one, or remove it.
      </span>

      <div className="flex flex-wrap gap-1.5">
        {(['bearerToken', 'basicAuth', 'headers'] as CredentialField[]).map((f) => {
          const on = configured.includes(f);
          return (
            <span
              key={f}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
                on ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' : 'border-black/10 dark:border-white/15 text-black/40 dark:text-white/40'
              }`}
            >
              {LABEL[f]}: {on ? 'configured' : 'not set'}
              {on && (
                <button
                  type="button"
                  aria-label={`Remove ${LABEL[f]}`}
                  disabled={saving}
                  onClick={() => void send({ [f]: f === 'bearerToken' ? '' : {} } as MappingCredentialsInput)}
                  className="underline text-black/50 dark:text-white/50 hover:text-red-500 disabled:opacity-50"
                >
                  remove
                </button>
              )}
            </span>
          );
        })}
      </div>

      <input
        aria-label="Bearer token"
        type="password"
        autoComplete="off"
        value={bearer}
        onChange={(e) => setBearer(e.target.value)}
        placeholder={configured.includes('bearerToken') ? 'New bearer token (replaces the current one)' : 'Bearer token'}
        className={inputCls}
      />
      <div className="flex gap-2">
        <input aria-label="Basic auth username" autoComplete="off" value={user} onChange={(e) => setUser(e.target.value)} placeholder="Basic auth username" className={`${inputCls} flex-1 min-w-0`} />
        <input aria-label="Basic auth password" type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Password" className={`${inputCls} flex-1 min-w-0`} />
      </div>
      <textarea
        aria-label="Custom headers"
        value={headerText}
        onChange={(e) => setHeaderText(e.target.value)}
        rows={2}
        spellCheck={false}
        placeholder={'X-Api-Key: …   (one “Name: value” per line; replaces the current headers)'}
        className={`${inputCls} font-mono text-[13px]`}
      />
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save credentials'}
        </Button>
        {savedNote && <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved.</span>}
      </div>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </AppCard>
  );
}
